// ─── Minerva Landing Page ────────────────────────────────────────────────────
import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import MinervaMark from '../home/components/MinervaMark';

// ─── CSS Styles ──────────────────────────────────────────────────────────────

function LandingStyles() {
  return (
    <style>{`
      /* ── Custom Properties (heredadas de :root — Códice nocturno) ── */
      .minerva-landing {
        font-family: var(--font-ui);
        background-color: var(--ink-900);
        color: var(--vellum);
        overflow-x: hidden;
        min-height: 100vh;
      }

      /* ── Grain / noise overlay ── */
      .minerva-grain-overlay {
        position: fixed;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='250' height='250'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='250' height='250' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E");
        opacity: 0.04;
        pointer-events: none;
        mix-blend-mode: overlay;
        z-index: 999;
      }

      /* ── @keyframes ── */
      @keyframes float-up {
        0%   { transform: translateY(0) translateX(0);    opacity: 0; }
        8%   { opacity: 1; }
        92%  { opacity: 1; }
        100% { transform: translateY(-105vh) translateX(15px); opacity: 0; }
      }
      @keyframes shimmer {
        0%   { background-position: -300% center; }
        100% { background-position: 300% center; }
      }
      @keyframes drift {
        0%, 100% { transform: translateX(0px) translateY(0px); }
        33%       { transform: translateX(6px)  translateY(-4px); }
        66%       { transform: translateX(-4px) translateY(3px); }
      }
      @keyframes spin-slow {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
      @keyframes badge-float {
        0%, 100% { transform: translateY(0px); }
        50%       { transform: translateY(-6px); }
      }
      @keyframes pulse-glow {
        0%, 100% { box-shadow: 0 0 18px rgba(201, 168, 84, 0.3), 0 4px 14px rgba(0,0,0,0.4); }
        50%       { box-shadow: 0 0 36px rgba(201, 168, 84, 0.55), 0 4px 20px rgba(0,0,0,0.4); }
      }
      @keyframes fade-in-up {
        from { opacity: 0; transform: translateY(24px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes bar-grow {
        from { width: 0%; }
        to   { width: var(--bar-width); }
      }

      /* ── Scroll reveal ── */
      .reveal-on-scroll {
        opacity: 0;
        transform: translateY(28px);
        transition: opacity 0.75s cubic-bezier(0.16, 1, 0.3, 1),
                    transform 0.75s cubic-bezier(0.16, 1, 0.3, 1);
      }
      .reveal-on-scroll.is-visible {
        opacity: 1;
        transform: translateY(0);
      }
      .reveal-delay-1 { transition-delay: 0.10s; }
      .reveal-delay-2 { transition-delay: 0.20s; }
      .reveal-delay-3 { transition-delay: 0.30s; }
      .reveal-delay-4 { transition-delay: 0.40s; }

      /* ── Particles ── */
      .particle {
        position: absolute;
        border-radius: 50%;
        background: var(--gold);
        pointer-events: none;
        animation: float-up var(--p-dur, 8s) var(--p-delay, 0s) infinite linear;
      }

      /* ── Navbar ── */
      .minerva-nav {
        position: fixed;
        top: 0; left: 0; right: 0;
        z-index: 1001;
        transition: background 0.4s, border-color 0.4s;
        border-bottom: 1px solid transparent;
      }
      .minerva-nav.scrolled {
        background: rgba(13, 27, 42, 0.88);
        backdrop-filter: blur(24px);
        -webkit-backdrop-filter: blur(24px);
        border-bottom-color: var(--gold-dim);
      }

      /* ── Buttons ── */
      .btn-gold {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: var(--gilt);
        color: var(--ink-900);
        font-weight: 600;
        font-family: var(--font-ui);
        letter-spacing: 0.02em;
        cursor: pointer;
        transition: transform 0.16s cubic-bezier(0.16,1,0.3,1), background 0.2s ease, box-shadow 0.2s ease;
      }
      .btn-gold:hover {
        background: var(--gilt-bright);
        box-shadow: 0 8px 26px -8px rgba(194, 162, 78, 0.5);
      }
      .btn-gold:active { transform: scale(0.97); }
      .btn-ghost {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--gilt-line);
        color: var(--vellum);
        font-family: var(--font-ui);
        cursor: pointer;
        transition: border-color 0.25s, background 0.25s, transform 0.16s;
      }
      .btn-ghost:hover {
        border-color: var(--gilt);
        background: var(--gilt-dim);
      }
      .btn-ghost:active { transform: scale(0.97); }

      /* ── Hero title (display serif, sin gradiente de texto) ── */
      .hero-title {
        font-family: var(--font-display);
        font-optical-sizing: auto;
        color: var(--vellum);
      }
      .hero-title em { font-style: italic; color: var(--gilt-bright); }

      /* ── Sigil SVG ── */
      .sigil-group {
        animation: drift 9s ease-in-out infinite;
      }
      .sigil-ring {
        transform-box: fill-box;
        transform-origin: center;
        animation: spin-slow 45s linear infinite;
      }
      .sigil-ring-inner {
        transform-box: fill-box;
        transform-origin: center;
        animation: spin-slow 30s linear infinite reverse;
      }

      /* ── Feature cards ── */
      .feature-card {
        background: linear-gradient(
          145deg,
          rgba(30, 58, 80, 0.55) 0%,
          rgba(15, 34, 54, 0.75) 100%
        );
        border: 1px solid var(--gold-dim);
        backdrop-filter: blur(12px);
        transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
      }
      .feature-card:hover {
        transform: translateY(-5px);
        border-color: rgba(201, 168, 84, 0.38);
        box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
      }

      /* ── Step cards ── */
      .step-card {
        background: linear-gradient(135deg, rgba(20, 40, 60, 0.7), rgba(13, 27, 42, 0.9));
        border: 1px solid var(--gold-dim);
        transition: transform 0.3s, border-color 0.3s;
      }
      .step-card:hover {
        transform: translateY(-4px);
        border-color: rgba(201, 168, 84, 0.4);
      }

      /* ── Trust bar ── */
      .trust-bar {
        border-top: 1px solid var(--gold-dim);
        border-bottom: 1px solid var(--gold-dim);
        background: linear-gradient(90deg, rgba(13,27,42,0), rgba(201,168,84,0.04), rgba(13,27,42,0));
      }

      /* ── Section headings ── */
      .section-label {
        font-family: var(--font-sans);
        font-size: 0.7rem;
        font-weight: 500;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        color: var(--gold);
      }
      .section-heading {
        font-family: var(--font-serif);
        color: var(--cream);
        line-height: 1.1;
      }

      /* ── Gold underline decoration ── */
      .gold-rule {
        height: 1px;
        background: linear-gradient(90deg, transparent, var(--gold), transparent);
        margin: 0 auto;
      }

      /* ── Quote section ── */
      .quote-section {
        background: linear-gradient(180deg, var(--navy-mid) 0%, var(--navy) 100%);
        border-top: 1px solid var(--gold-dim);
        border-bottom: 1px solid var(--gold-dim);
        position: relative;
        overflow: hidden;
      }
      .quote-section::before {
        content: '"';
        position: absolute;
        top: -40px;
        left: 50%;
        transform: translateX(-50%);
        font-family: var(--font-serif);
        font-size: 20rem;
        color: rgba(201, 168, 84, 0.04);
        line-height: 1;
        pointer-events: none;
        user-select: none;
      }

      /* ── Professors section ── */
      .professors-section {
        background: linear-gradient(135deg, #091622 0%, #0D1B2A 100%);
      }

      /* ── Analytics mock panel ── */
      .analytics-panel {
        background: linear-gradient(145deg, rgba(20,40,60,0.8), rgba(10,22,35,0.95));
        border: 1px solid var(--gold-dim);
        box-shadow: 0 32px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,168,84,0.1);
      }
      .analytics-bar-track {
        height: 5px;
        border-radius: 3px;
        background: rgba(201, 168, 84, 0.12);
        overflow: hidden;
      }
      .analytics-bar-fill {
        height: 100%;
        border-radius: 3px;
        background: linear-gradient(90deg, var(--gold), var(--gold-light));
        animation: bar-grow 1.5s cubic-bezier(0.16,1,0.3,1) both;
        animation-play-state: paused;
      }
      .analytics-bar-fill.animated {
        animation-play-state: running;
      }

      /* ── Floating badge ── */
      .floating-badge {
        animation: badge-float 3s ease-in-out infinite;
        border: 1px solid rgba(201, 168, 84, 0.3);
        background: rgba(13, 27, 42, 0.7);
        backdrop-filter: blur(8px);
      }

      /* ── Ambient glow blobs ── */
      .glow-blob {
        position: absolute;
        border-radius: 50%;
        filter: blur(80px);
        pointer-events: none;
      }

      /* ── Radial divider ── */
      .radial-divider {
        height: 1px;
        background: linear-gradient(90deg, transparent 0%, var(--gold-dim) 30%, rgba(201,168,84,0.3) 50%, var(--gold-dim) 70%, transparent 100%);
      }

      /* ── Icon circle ── */
      .icon-circle {
        border: 1px solid rgba(201, 168, 84, 0.3);
        background: rgba(201, 168, 84, 0.08);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        flex-shrink: 0;
      }

      /* ── Bullet list item ── */
      .professor-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 14px 0;
        border-bottom: 1px solid rgba(201, 168, 84, 0.08);
      }
      .professor-item:last-child { border-bottom: none; }

      /* ── Footer ── */
      .landing-footer {
        border-top: 1px solid var(--gold-dim);
        background: rgba(7, 16, 27, 0.95);
      }

      /* ── Pulsing dot ── */
      @keyframes dot-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50%       { opacity: 0.5; transform: scale(0.8); }
      }
      .pulse-dot {
        width: 6px; height: 6px;
        border-radius: 50%;
        background: var(--sage);
        animation: dot-pulse 2s ease-in-out infinite;
      }

      /* ── Hero entrance animation ── */
      .hero-enter { animation: fade-in-up 0.9s cubic-bezier(0.16, 1, 0.3, 1) both; }
      .hero-enter-1 { animation-delay: 0.1s; }
      .hero-enter-2 { animation-delay: 0.25s; }
      .hero-enter-3 { animation-delay: 0.4s; }
      .hero-enter-4 { animation-delay: 0.55s; }
      .hero-enter-5 { animation-delay: 0.7s; }
    `}</style>
  );
}

// ─── SVG Components ───────────────────────────────────────────────────────────

function MinervaOwlSVG({ size = 32 }: { size?: number }) {
  return <MinervaMark size={size} />;
}

function MinervaSignilSVG() {
  return (
    <svg
      viewBox="0 0 340 340"
      width="320"
      height="320"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', margin: '0 auto' }}
    >
      {/* Outer ring — rotates clockwise */}
      <g className="sigil-ring">
        <circle cx="170" cy="170" r="155" stroke="#C9A854" strokeWidth="0.6" opacity="0.35" />
        {/* Diamond markers at cardinal points */}
        <polygon points="170,12  174,16  170,20  166,16"  fill="#C9A854" opacity="0.7" />
        <polygon points="170,320 174,324 170,328 166,324" fill="#C9A854" opacity="0.7" />
        <polygon points="12,170  16,174  20,170  16,166"  fill="#C9A854" opacity="0.7" />
        <polygon points="320,170 324,174 328,170 324,166" fill="#C9A854" opacity="0.7" />
        {/* Tick marks at 45° */}
        <circle cx="279.2" cy="60.8"  r="2" fill="#C9A854" opacity="0.4" />
        <circle cx="279.2" cy="279.2" r="2" fill="#C9A854" opacity="0.4" />
        <circle cx="60.8"  cy="279.2" r="2" fill="#C9A854" opacity="0.4" />
        <circle cx="60.8"  cy="60.8"  r="2" fill="#C9A854" opacity="0.4" />
      </g>

      {/* Inner ring — rotates counter-clockwise */}
      <g className="sigil-ring-inner">
        <circle cx="170" cy="170" r="130" stroke="#C9A854" strokeWidth="0.4" opacity="0.2" strokeDasharray="4 8" />
      </g>

      {/* Owl — drifts gently */}
      <g className="sigil-group">
        {/* Horizontal separator line */}
        <line x1="100" y1="170" x2="240" y2="170" stroke="#C9A854" strokeWidth="0.5" opacity="0.25" />
        {/* Vertical axis */}
        <line x1="170" y1="80" x2="170" y2="270" stroke="#C9A854" strokeWidth="0.5" opacity="0.15" />

        {/* Body */}
        <polygon
          points="170,258  128,272  212,272"
          fill="rgba(201,168,84,0.08)"
          stroke="#C9A854"
          strokeWidth="1"
          strokeLinejoin="round"
        />
        {/* Wing left */}
        <path d="M 128 255 Q 100 230 106 192" stroke="#C9A854" strokeWidth="0.9" fill="none" opacity="0.5" strokeLinecap="round" />
        {/* Wing right */}
        <path d="M 212 255 Q 240 230 234 192" stroke="#C9A854" strokeWidth="0.9" fill="none" opacity="0.5" strokeLinecap="round" />

        {/* Head */}
        <circle cx="170" cy="180" r="42" fill="rgba(13,27,42,0.5)" stroke="#C9A854" strokeWidth="1.2" />

        {/* Left eye */}
        <circle cx="152" cy="178" r="14" fill="rgba(201,168,84,0.08)" stroke="#C9A854" strokeWidth="1.2" />
        <circle cx="152" cy="178" r="5.5"  fill="#C9A854" opacity="0.85" />
        <circle cx="154" cy="176" r="2"    fill="rgba(13,27,42,0.8)" />

        {/* Right eye */}
        <circle cx="188" cy="178" r="14" fill="rgba(201,168,84,0.08)" stroke="#C9A854" strokeWidth="1.2" />
        <circle cx="188" cy="178" r="5.5"  fill="#C9A854" opacity="0.85" />
        <circle cx="190" cy="176" r="2"    fill="rgba(13,27,42,0.8)" />

        {/* Beak */}
        <polygon points="170,190  163,205  177,205" fill="#C9A854" opacity="0.8" />

        {/* Left ear tuft */}
        <polygon points="154,138  146,118  162,118" fill="none" stroke="#C9A854" strokeWidth="1.2" strokeLinejoin="round" />
        {/* Right ear tuft */}
        <polygon points="186,138  178,118  194,118" fill="none" stroke="#C9A854" strokeWidth="1.2" strokeLinejoin="round" />

        {/* Decorative arc under eyes */}
        <path d="M 145 196 Q 170 208 195 196" stroke="#C9A854" strokeWidth="0.7" fill="none" opacity="0.35" />
      </g>
    </svg>
  );
}

// ─── Particle System ──────────────────────────────────────────────────────────

interface ParticleConfig {
  id: number;
  top: string;
  left: string;
  size: number;
  delay: string;
  duration: string;
  opacity: number;
}

function generateParticles(count: number): ParticleConfig[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    top:      `${((i * 7.31 + 11.7) % 100).toFixed(1)}%`,
    left:     `${((i * 13.67 + 5.3) % 100).toFixed(1)}%`,
    size:     (i % 3 === 0) ? 2.5 : (i % 3 === 1) ? 1.5 : 1,
    delay:    `${((i * 0.38) % 9).toFixed(2)}s`,
    duration: `${(7 + (i % 7)).toFixed(1)}s`,
    opacity:  (i % 5 === 0) ? 0.6 : 0.25,
  }));
}

function ParticleField({ particles }: { particles: ParticleConfig[] }) {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {particles.map(p => (
        <div
          key={p.id}
          className="particle"
          style={{
            top:      p.top,
            left:     p.left,
            width:    `${p.size}px`,
            height:   `${p.size}px`,
            opacity:  p.opacity,
            ['--p-delay' as any]: p.delay,
            ['--p-dur'   as any]: p.duration,
          }}
        />
      ))}
    </div>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────

function LandingNav() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav className={`minerva-nav ${scrolled ? 'scrolled' : ''}`}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }} className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          <MinervaOwlSVG size={30} />
          <span style={{ fontFamily: 'var(--font-serif)', color: 'var(--gold)', fontSize: '1.45rem', letterSpacing: '0.06em' }}>
            Minerva
          </span>
        </div>
        <Link
          to="/app"
          className="btn-gold text-sm px-5 py-2"
          style={{ borderRadius: '100px', textDecoration: 'none' }}
        >
          Acceder
        </Link>
      </div>
    </nav>
  );
}

// ─── Hero Section ─────────────────────────────────────────────────────────────

function HeroSection({ particles }: { particles: ParticleConfig[] }) {
  const scrollToHowItWorks = () => {
    document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: `radial-gradient(ellipse 80% 80% at 50% -10%, rgba(201,168,84,0.08) 0%, transparent 70%), var(--navy)`,
      }}
    >
      <ParticleField particles={particles} />

      {/* Ambient glow blobs */}
      <div className="glow-blob" style={{ width: '600px', height: '600px', background: 'rgba(201,168,84,0.05)', top: '-150px', left: '-100px' }} />
      <div className="glow-blob" style={{ width: '500px', height: '500px', background: 'rgba(122,158,142,0.04)', bottom: '-100px', right: '-80px' }} />

      <div
        style={{ maxWidth: '1100px', margin: '0 auto', width: '100%', padding: '140px 24px 80px', position: 'relative', zIndex: 10 }}
        className="flex flex-col items-center text-center gap-6"
      >
        {/* Floating badge */}
        <div
          className="floating-badge hero-enter hero-enter-1"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '100px', fontSize: '0.78rem' }}
        >
          <span className="pulse-dot" />
          <span style={{ color: 'var(--text-muted)', letterSpacing: '0.05em' }}>
            Powered by RAG · Verificado por tu docente
          </span>
        </div>

        {/* Main title */}
        <h1
          className="hero-title hero-enter hero-enter-2"
          style={{ fontSize: 'clamp(3.4rem, 9vw, 6rem)', letterSpacing: '0.01em', margin: 0, fontWeight: 500, lineHeight: 1.02 }}
        >
          Minerva
        </h1>

        {/* Subtitle */}
        <p
          className="hero-enter hero-enter-3"
          style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.2rem, 3vw, 1.75rem)', color: 'var(--cream)', opacity: 0.85, fontWeight: 400, maxWidth: '600px', lineHeight: 1.3, margin: 0 }}
        >
          Tu asistente académico, entrenado con tu temario
        </p>

        {/* Description */}
        <p
          className="hero-enter hero-enter-4"
          style={{ fontSize: '1rem', color: 'var(--text-muted)', maxWidth: '520px', lineHeight: 1.75, margin: 0 }}
        >
          Respuestas fiables extraídas de los materiales de tus asignaturas. Sin alucinaciones, sin contenido externo no verificado.
        </p>

        {/* CTA buttons */}
        <div className="hero-enter hero-enter-5 flex flex-wrap gap-4 justify-center" style={{ marginTop: '8px' }}>
          <Link
            to="/app"
            className="btn-gold text-base px-7 py-3"
            style={{ borderRadius: '100px', textDecoration: 'none', fontSize: '1rem' }}
          >
            <span style={{ marginRight: '8px' }}>Comenzar ahora</span>
            <i className="ri-arrow-right-line" />
          </Link>
          <button
            onClick={scrollToHowItWorks}
            className="btn-ghost text-base px-7 py-3"
            style={{ borderRadius: '100px', fontSize: '1rem', background: 'none', border: '1px solid rgba(201,168,84,0.35)' }}
          >
            Saber más
          </button>
        </div>

        {/* Sigil */}
        <div
          className="hero-enter"
          style={{ marginTop: '48px', opacity: 0.9, animationDelay: '0.85s' }}
        >
          <MinervaSignilSVG />
        </div>
      </div>
    </section>
  );
}

// ─── Trust Bar ────────────────────────────────────────────────────────────────

function TrustBar() {
  const items = [
    { icon: 'ri-book-open-line',    label: '7 asignaturas' },
    { icon: 'ri-shield-check-line', label: 'Verificado por docentes' },
    { icon: 'ri-lock-line',         label: 'Privado y seguro' },
    { icon: 'ri-links-line',        label: 'Respuestas con fuentes' },
  ];

  return (
    <div className="trust-bar">
      <div
        style={{ maxWidth: '1100px', margin: '0 auto', padding: '18px 24px' }}
        className="flex flex-wrap justify-center gap-6 md:gap-10"
      >
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <i className={`${item.icon} text-base`} style={{ color: 'var(--gold)' }} />
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', letterSpacing: '0.04em', fontWeight: 500 }}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── How It Works Section ─────────────────────────────────────────────────────

function HowItWorksSection() {
  const steps = [
    {
      num: '01',
      icon: 'ri-upload-cloud-line',
      title: 'Los docentes suben los materiales',
      desc: 'Apuntes, diapositivas, lecturas y ejercicios quedan indexados de forma privada en la plataforma.',
    },
    {
      num: '02',
      icon: 'ri-chat-3-line',
      title: 'Los estudiantes preguntan libremente',
      desc: 'El asistente conversacional responde en lenguaje natural, disponible las 24 horas.',
    },
    {
      num: '03',
      icon: 'ri-verified-badge-line',
      title: 'Respuestas verificadas con fuentes',
      desc: 'Cada respuesta cita el documento exacto del que procede, garantizando la trazabilidad.',
    },
  ];

  return (
    <section
      id="como-funciona"
      style={{ padding: 'clamp(64px, 10vw, 112px) 24px', background: 'var(--navy)' }}
    >
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div className="text-center mb-12">
          <p className="section-label reveal-on-scroll">Proceso</p>
          <h2
            className="section-heading reveal-on-scroll reveal-delay-1"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.2rem)', marginTop: '8px', fontWeight: 600 }}
          >
            ¿Cómo funciona?
          </h2>
          <div className="gold-rule reveal-on-scroll reveal-delay-2" style={{ width: '60px', marginTop: '20px' }} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative">
          {/* Connector line on desktop */}
          <div
            className="hidden md:block"
            style={{
              position: 'absolute',
              top: '52px',
              left: '25%',
              right: '25%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(201,168,84,0.3), rgba(201,168,84,0.3), transparent)',
              zIndex: 0,
            }}
          />

          {steps.map((step, i) => (
            <div
              key={i}
              className={`step-card reveal-on-scroll reveal-delay-${i + 1} relative`}
              style={{ borderRadius: '20px', padding: '32px 28px', zIndex: 1 }}
            >
              {/* Step number (decorative) */}
              <div
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '4.5rem',
                  fontWeight: 700,
                  color: 'rgba(201,168,84,0.08)',
                  lineHeight: 1,
                  position: 'absolute',
                  top: '16px',
                  right: '20px',
                  userSelect: 'none',
                }}
              >
                {step.num}
              </div>

              {/* Icon */}
              <div
                className="icon-circle"
                style={{ width: '52px', height: '52px', marginBottom: '20px' }}
              >
                <i className={`${step.icon} text-xl`} style={{ color: 'var(--gold)' }} />
              </div>

              <h3
                style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', color: 'var(--cream)', fontWeight: 600, marginBottom: '10px', lineHeight: 1.2 }}
              >
                {step.title}
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.92rem', lineHeight: 1.7 }}>
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── For Students Section ─────────────────────────────────────────────────────

function ForStudentsSection() {
  const features = [
    {
      icon: 'ri-book-read-line',
      title: 'Del temario oficial',
      desc: 'Todas las respuestas provienen exclusivamente de los materiales subidos por tu profesor. Sin contenido externo ni invenciones.',
    },
    {
      icon: 'ri-file-search-line',
      title: 'Fuentes siempre visibles',
      desc: 'Cada respuesta muestra la diapositiva, el apunte o el documento exacto del que procede. Transparencia total.',
    },
    {
      icon: 'ri-time-line',
      title: 'Disponible 24/7',
      desc: 'Estudia y resuelve dudas a cualquier hora, sin esperar a tutorías ni correos que tardan días.',
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Acceso exclusivo y privado',
      desc: 'Solo el alumnado autorizado accede a la plataforma. Tus consultas son anónimas para el profesorado.',
    },
  ];

  return (
    <section style={{ padding: 'clamp(64px, 10vw, 112px) 24px', background: 'var(--navy-mid)' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="section-label reveal-on-scroll">Para el alumnado</p>
            <h2
              className="section-heading reveal-on-scroll reveal-delay-1"
              style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 600, marginTop: '8px' }}
            >
              Un asistente en el que<br />puedes confiar
            </h2>
          </div>
          <p
            className="reveal-on-scroll reveal-delay-2"
            style={{ color: 'var(--text-muted)', fontSize: '0.95rem', maxWidth: '320px', lineHeight: 1.7 }}
          >
            Construido para complementar —nunca sustituir— el trabajo del aula.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {features.map((f, i) => (
            <div
              key={i}
              className={`feature-card reveal-on-scroll reveal-delay-${i + 1}`}
              style={{ borderRadius: '18px', padding: '28px 26px' }}
            >
              <div className="icon-circle" style={{ width: '44px', height: '44px', marginBottom: '16px' }}>
                <i className={`${f.icon} text-lg`} style={{ color: 'var(--gold)' }} />
              </div>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.15rem', color: 'var(--cream)', fontWeight: 600, marginBottom: '8px' }}>
                {f.title}
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Mock Analytics Panel ─────────────────────────────────────────────────────

function MockAnalyticsPanel() {
  const panelRef = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimated(true); obs.disconnect(); } },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const bars = [
    { label: 'Complejidad algorítmica', pct: 87 },
    { label: 'Recursividad y pilas',    pct: 74 },
    { label: 'Grafos y árboles',        pct: 61 },
    { label: 'Ordenación avanzada',     pct: 48 },
    { label: 'Programación dinámica',   pct: 39 },
  ];

  return (
    <div
      ref={panelRef}
      className="analytics-panel"
      style={{ borderRadius: '20px', padding: '28px 26px' }}
    >
      {/* Panel header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p style={{ fontFamily: 'var(--font-serif)', color: 'var(--gold)', fontSize: '1.1rem', fontWeight: 600 }}>
            Consultas por tema
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '2px' }}>
            Últimos 30 días · Anónimas
          </p>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(201,168,84,0.5)' }} />
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(201,168,84,0.3)' }} />
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(201,168,84,0.15)' }} />
        </div>
      </div>

      {/* Bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {bars.map((b, i) => (
          <div key={i}>
            <div className="flex justify-between" style={{ marginBottom: '5px' }}>
              <span style={{ color: 'var(--cream)', fontSize: '0.82rem', opacity: 0.8 }}>{b.label}</span>
              <span style={{ color: 'var(--gold)', fontSize: '0.78rem', fontWeight: 500 }}>{b.pct}%</span>
            </div>
            <div className="analytics-bar-track">
              <div
                className={`analytics-bar-fill ${animated ? 'animated' : ''}`}
                style={{ ['--bar-width' as any]: `${b.pct}%`, animationDelay: `${i * 0.12}s` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer note */}
      <p style={{ color: 'var(--text-muted)', fontSize: '0.73rem', marginTop: '16px', borderTop: '1px solid rgba(201,168,84,0.1)', paddingTop: '12px' }}>
        <i className="ri-lock-line" style={{ marginRight: '4px' }} />
        Los datos se agregan de forma anónima. Ninguna consulta está vinculada a un estudiante concreto.
      </p>
    </div>
  );
}

// ─── For Professors Section ───────────────────────────────────────────────────

function ForProfessorsSection() {
  const items = [
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Dudas recurrentes detectadas',
      desc: 'Descubre automáticamente qué conceptos generan más consultas y adapta tus explicaciones en consecuencia.',
    },
    {
      icon: 'ri-eye-off-line',
      title: 'Estadísticas completamente anónimas',
      desc: 'El profesorado recibe datos agregados. Nunca se asocia ninguna consulta a un estudiante concreto.',
    },
    {
      icon: 'ri-loop-right-line',
      title: 'Mejora continua de la docencia',
      desc: 'Un canal de retroalimentación silencioso que funciona incluso cuando los estudiantes no asisten a tutorías.',
    },
  ];

  return (
    <section
      className="professors-section"
      style={{ padding: 'clamp(64px, 10vw, 112px) 24px' }}
    >
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text side */}
          <div>
            <p className="section-label reveal-on-scroll">Para el profesorado</p>
            <h2
              className="section-heading reveal-on-scroll reveal-delay-1"
              style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 600, marginTop: '8px', marginBottom: '16px' }}
            >
              Conoce lo que el aula<br />no te dice
            </h2>
            <p
              className="reveal-on-scroll reveal-delay-2"
              style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.75, marginBottom: '32px', maxWidth: '460px' }}
            >
              La IA conversacional ha cambiado el paradigma: los estudiantes consultan menos al profesorado. Con Minerva recuperas ese canal de retroalimentación.
            </p>

            <div className="reveal-on-scroll reveal-delay-3">
              {items.map((item, i) => (
                <div key={i} className="professor-item">
                  <div className="icon-circle" style={{ width: '38px', height: '38px', marginTop: '2px' }}>
                    <i className={`${item.icon} text-sm`} style={{ color: 'var(--gold)' }} />
                  </div>
                  <div>
                    <h4 style={{ color: 'var(--cream)', fontSize: '0.95rem', fontWeight: 500, marginBottom: '4px' }}>
                      {item.title}
                    </h4>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.87rem', lineHeight: 1.65 }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Analytics panel side */}
          <div className="reveal-on-scroll reveal-delay-2">
            <MockAnalyticsPanel />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Mission Quote Section ────────────────────────────────────────────────────

function MissionQuoteSection() {
  return (
    <section className="quote-section" style={{ padding: 'clamp(72px, 12vw, 120px) 24px' }}>
      <div
        style={{ maxWidth: '820px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}
      >
        <div className="gold-rule reveal-on-scroll" style={{ width: '40px', marginBottom: '40px' }} />
        <blockquote
          className="reveal-on-scroll reveal-delay-1"
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(1.4rem, 3.5vw, 2.1rem)',
            fontStyle: 'italic',
            color: 'var(--cream)',
            lineHeight: 1.55,
            fontWeight: 400,
            margin: 0,
            opacity: 0.9,
          }}
        >
          Construida para que cada respuesta tenga una fuente, cada duda tenga un origen, y cada estudiante tenga acceso al saber de su curso.
        </blockquote>
        <div className="gold-rule reveal-on-scroll reveal-delay-2" style={{ width: '40px', marginTop: '40px' }} />
      </div>
    </section>
  );
}

// ─── Final CTA Section ────────────────────────────────────────────────────────

function FinalCTASection() {
  return (
    <section style={{ padding: 'clamp(80px, 12vw, 128px) 24px', background: 'var(--navy)' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto', textAlign: 'center' }}>
        <div
          className="reveal-on-scroll"
          style={{
            border: '1px solid var(--gold-dim)',
            borderRadius: '28px',
            padding: 'clamp(40px, 7vw, 64px) clamp(24px, 6vw, 56px)',
            background: 'linear-gradient(145deg, rgba(30,58,80,0.4), rgba(13,27,42,0.7))',
            backdropFilter: 'blur(16px)',
          }}
        >
          {/* Owl emblem */}
          <div style={{ marginBottom: '24px' }}>
            <MinervaOwlSVG size={52} />
          </div>

          <h2
            className="section-heading reveal-on-scroll reveal-delay-1"
            style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, marginBottom: '14px' }}
          >
            Listo para empezar
          </h2>
          <p
            className="reveal-on-scroll reveal-delay-2"
            style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1.7, marginBottom: '32px' }}
          >
            Accede a tu asistente académico y estudia con la confianza de que cada respuesta tiene respaldo en tu temario.
          </p>

          <Link
            to="/app"
            className="btn-gold reveal-on-scroll reveal-delay-3 text-base px-9 py-4"
            style={{ borderRadius: '100px', textDecoration: 'none', fontSize: '1.05rem' }}
          >
            Acceder a Minerva
            <i className="ri-arrow-right-line" style={{ marginLeft: '8px' }} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function LandingFooter() {
  return (
    <footer className="landing-footer" style={{ padding: '28px 24px' }}>
      <div
        style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}
      >
        <div className="flex items-center gap-2">
          <MinervaOwlSVG size={22} />
          <span style={{ fontFamily: 'var(--font-serif)', color: 'var(--gold)', fontSize: '1.1rem', letterSpacing: '0.05em' }}>
            Minerva
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginLeft: '4px' }}>
            · minervibe.ai
          </span>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
          Proyecto docente · Universidad de Alicante
        </p>
      </div>
    </footer>
  );
}

// ─── Root Component ───────────────────────────────────────────────────────────

export default function Landing() {
  const particles = useMemo(() => generateParticles(55), []);

  // Intersection Observer for scroll reveals
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.12 }
    );
    // Small delay to allow DOM to paint
    const timer = setTimeout(() => {
      document.querySelectorAll('.reveal-on-scroll').forEach(el => observer.observe(el));
    }, 100);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <LandingStyles />
      {/* Grain overlay */}
      <div className="minerva-grain-overlay" />

      <div className="minerva-landing">
        <LandingNav />
        <HeroSection particles={particles} />
        <TrustBar />
        <HowItWorksSection />
        <ForStudentsSection />
        <ForProfessorsSection />
        <MissionQuoteSection />
        <FinalCTASection />
        <LandingFooter />
      </div>
    </>
  );
}
